
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface SearchResult {
  title: string;
  path: string;
  description: string;
  category: string;
}

export default function SiteSearch() {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [results, setResults] = useState<SearchResult[]>([]);
  const navigate = useNavigate();

  const searchData: SearchResult[] = [
    { title: 'ホーム', path: '/', description: 'みんなの薬局のトップページ', category: 'メイン' },
    { title: 'サービス一覧', path: '/services', description: '提供している全サービスの概要', category: 'メイン' },
    { title: '健康サポート', path: '/health-support', description: '患者さん向けの健康支援サービス', category: '患者向け' },
    { title: 'OTC医薬品', path: '/otc', description: '市販薬の情報と購入サポート', category: '患者向け' },
    { title: '漢方相談', path: '/kampo', description: '漢方薬の相談とサポート', category: '患者向け' },
    { title: '薬剤師向けリソース', path: '/pharmacist-resources', description: '薬剤師専門の情報とツール', category: '薬剤師向け' },
    { title: '無菌調剤', path: '/sterile-preparation', description: '無菌調剤の技術と設備情報', category: '薬剤師向け' },
    { title: '在宅医療', path: '/home-care', description: '在宅医療サポートサービス', category: '薬剤師向け' },
    { title: '薬局DX', path: '/dx', description: 'デジタル化支援サービス', category: '薬剤師向け' },
    { title: '専門薬局', path: '/specialty-pharmacy', description: '専門薬局の情報とサービス', category: '薬剤師向け' },
    { title: '添付文書検索', path: '/package-insert-search', description: '医薬品の添付文書検索ツール', category: 'ツール' },
    { title: '薬価サーチ', path: '/drug-price-search', description: '医薬品の薬価検索ツール', category: 'ツール' },
    { title: '医療・介護用品サイト', path: '/medical-purchase', description: '医療・介護用品の購入サイト情報', category: 'ツール' },
    { title: '医薬品卸サイト', path: '/pharmaceutical-wholesale', description: '医薬品卸業者の情報', category: 'ツール' },
    { title: '情報提供', path: '/information', description: '公的機関リンクと最新情報', category: '情報' },
    { title: '転職・派遣情報', path: '/career', description: '薬剤師転職・派遣・キャリア支援情報', category: '情報' },
    { title: 'エンタメ', path: '/entertainment', description: '薬剤師向けエンターテイメント', category: '情報' },
    { title: 'ビースト君ブログ', path: '/beast-blog', description: '筋肉薬剤師ビースト君のブログ', category: 'ブログ' },
    { title: '薬の効果的な飲み方', path: '/beast-blog/effective-medication', description: '薬の正しい服用方法について', category: 'ブログ' },
    { title: '筋トレと健康管理', path: '/beast-blog/health-management', description: '薬剤師が教える健康管理法', category: 'ブログ' },
    { title: '市販薬選びのコツ', path: '/beast-blog/otc-selection', description: 'OTC医薬品の選び方ガイド', category: 'ブログ' },
    { title: '冬季メンタルヘルス', path: '/beast-blog/winter-mental-health', description: '冬季うつ病の対策と予防', category: 'ブログ' },
    { title: '多職種連携', path: '/beast-blog/multidisciplinary-collaboration', description: '薬剤師の多職種連携について', category: 'ブログ' }
  ];

  const handleSearch = (term: string) => {
    if (term.trim() === '') {
      setResults([]);
      return;
    }

    const filtered = searchData.filter(item =>
      item.title.toLowerCase().includes(term.toLowerCase()) ||
      item.description.toLowerCase().includes(term.toLowerCase()) ||
      item.category.toLowerCase().includes(term.toLowerCase())
    );

    setResults(filtered.slice(0, 8)); // 最大8件まで表示
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    handleSearch(value);
    setIsOpen(value.trim() !== '');
  };

  const handleResultClick = (path: string) => {
    navigate(path);
    setSearchTerm('');
    setResults([]);
    setIsOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      setSearchTerm('');
      setResults([]);
    }
  };

  return (
    <div className="relative">
      <div className="relative">
        <input
          type="text"
          value={searchTerm}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder="サイト内検索..."
          className="w-64 px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
        />
        <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
          <i className="ri-search-line text-gray-400"></i>
        </div>
      </div>

      {/* Search Results Dropdown */}
      {isOpen && results.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-96 overflow-y-auto">
          {results.map((result, index) => (
            <button
              key={index}
              onClick={() => handleResultClick(result.path)}
              className="w-full px-4 py-3 text-left hover:bg-gray-50 border-b border-gray-100 last:border-b-0 cursor-pointer"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900 mb-1">{result.title}</h4>
                  <p className="text-sm text-gray-600 line-clamp-2">{result.description}</p>
                </div>
                <span className="ml-2 px-2 py-1 bg-teal-100 text-teal-800 text-xs rounded-full whitespace-nowrap">
                  {result.category}
                </span>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* No Results */}
      {isOpen && searchTerm.trim() !== '' && results.length === 0 && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 p-4">
          <p className="text-gray-500 text-center">検索結果が見つかりませんでした</p>
        </div>
      )}

      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => {
            setIsOpen(false);
            setSearchTerm('');
            setResults([]);
          }}
        />
      )}
    </div>
  );
}
